import type {
  Affix,
  AffixId,
  ApplyResult,
  BalanceOverrides,
  BasicDie,
  CombatThrowEntry,
  ContainerEntry,
  DerivedStats,
  DieResult,
  DieSides,
  DieSpellId,
  EnemyState,
  EnemyTypeId,
  ForgeOutcome,
  ForgePending,
  ForgeRecipeId,
  ForgeRecipeView,
  ForgeRequest,
  GameEvents,
  GameState,
  MachineDef,
  MachineId,
  MasterDie,
  MaterialId,
  NodeAvailability,
  Phase,
  Rarity,
  SlotSpellId,
  ThrowBreakdown,
  ThrowResult,
  TriggeredSpell,
  WorldRef,
} from './types';
import { BALANCE } from '../data/balance';
import { AFFIX_BY_ID, namedDieName } from '../data/affixes';
import { resolveBalance, type ResolvedBalance } from './balance';
import {
  buildThrow,
  diceMasterMultiplier,
  isExtraRoll,
  killBonuses,
  mutilationRoll,
  ownBasicSum,
  ownTotal,
  poisonOf,
  streakMultiplier,
  type ThrowOptions,
} from './combat';
import { affixValueOf as affixValueOfFn, hasAffix } from './affixes';
import * as container from './container';
import { createMasterDie, createStarterDie, dieLevelFactor as dieLevelFactorFn, namedAffixOf } from './dice';
import { abyssGateLevel, expeditionGoldRef as expeditionGoldRefFn, waveOf } from './economy';
import * as forge from './forge';
import { MACHINES } from '../data/machines';
import { MATERIAL_BY_ID, isMaterialId } from '../data/materials';
import {
  activeMaterialBonuses,
  hasMaterialBonus,
  rubberSlots,
  type ActiveMaterialBonus,
} from './materials';
import { grimoireFactor } from './combos';
import { describeSpell, type SpellText } from './spellText';
import { NODE_BY_ID } from '../data/nodes';
import {
  addPoison,
  applyMutilation,
  chanceForDt,
  damageEnemy,
  enemyEffects,
  enemyHpAt,
  findEffect,
  mutilationFactor,
  rollEnemy,
  scaleEnemyHp,
  tickMutilation,
  tickPoison,
  tickRegen,
  voidEnemyEffect,
} from './enemies';
import { expectedThrow, type ExpectedThrow } from './expected';
import { bestDie, isMachineUnlocked, lotCost, machineById } from './gacha';
import * as phases from './phases';
import type { PhaseCtx } from './phases';
import { Rng, randomSeed } from './rng';
import {
  SAVE_VERSION,
  buildSave,
  emptySave,
  emptyStats,
  normalizeSave,
  type SaveData, serializeSave, verifySaveSignature, } from './save';
import * as shop from './shop';
import type { EquipCheck, RecycleFilter, RecycleOutcome } from './shop';
import {
  ARTISAN_NODE_ID,
  abyssOpenings,
  canBuyNode as canBuyNodeFn,
  describeNodeLevel,
  grantArtisanLevel,
  nodeLevel,
  type NodeLevelText,
} from './skilltree';
import { getDerivedStats } from './stats';
import { FREE_LEVELS, gateBlocks, gateStatus as gateStatusOf, isFreeLevel, type GateStatus } from './gate';
import { currentBossId, isEndless, worldName as worldNameOf, worldOf } from './world';

export type { EquipCheck, RecycleFilter, RecycleOutcome };

// Mur payant (v0.55.0, A112).
export { FREE_LEVELS, gateBlocks, gateStatusOf as gateStatus, isFreeLevel };
export type { GateStatus };

export type {
  ApplyResult,
  BalanceOverrides,
  BasicDie,
  CombatThrowEntry,
  ContainerEntry,
  DerivedStats,
  DieResult,
  EnemyState,
  ExpectedThrow,
  GameEvents,
  GameState,
  NodeLevelText,
  Phase,
  SaveData,
  ThrowBreakdown,
  ThrowResult,
  TriggeredSpell,
  WorldRef,
};

/** Spécification d'un dé forgé à la main (panneau de debug — COMMENTS §7). */
export interface ForgeSpec {
  sides: DieSides;
  rarity: Rarity;
  affixes: { id: AffixId; value: number }[];
  name?: string;
  /** Sorts imposés au dé ; par défaut ils sont tirés selon la rareté (A3). */
  spells?: DieSpellId[];
  /** Niveau du dé (A94-5) : entier borné à 1 … 60 ; 0 ou absent = aucun. */
  level?: number;
  /** Matériau (A94) : identifiant connu, sinon ignoré. */
  material?: MaterialId;
  /** Dé Enrichi (A94). */
  enriched?: boolean;
  /**
   * Dé **Corrupted** (A98-6) : forgeable, ×1000 dans `D_L`. Sans ce champ, le
   * forgeur de debug ne rend jamais un dé Corrupted.
   */
  corrupted?: boolean;
}

type Listener = (payload: never) => void;

/** Nœuds qui changent le contenu du contenant (événement `container`). */
const CONTAINER_NODES = new Set([
  'ars_goblet',
  'ars_hex',
  'ars_bowl',
  'ars_bucket',
  'ars_dodeca',
  'ars_barrow',
  'ars_icosa',
]);

/** Décomposition neutre du lancer vide (tous les facteurs à 1, tout à 0). */
const EMPTY_BREAKDOWN: ThrowBreakdown = {
  baseMaster: 0,
  baseBasic: 0,
  flat: 0,
  dieLevelBonus: 0,
  corruptedBonus: 0,
  DL: 0,
  DC: 1,
  DS: 1,
  MP: 1,
  MT: 1,
  affixMult: 1,
  minionMult: 1,
  infernoMult: 1,
  primesMult: 1,
  total: 0,
  critAdditive: [],
  critMultipliers: [],
  spellEffectMultipliers: [],
  critDivisor: 1,
  basicDivisor: 1,
  armorDivisor: 1,
  spellsHalved: false,
  prepMult: 1,
  blockedSlots: 0,
  diceMasterMult: 1,
  critStreakMult: 1,
  spellStreakMult: 1,
  backhoeBasic: 0,
  grimoireNumbersMult: 1,
  grimoireFacesMult: 1,
  brokenMult: 1,
  ironBasicMult: 1,
  woodBasicCarry: 0,
  voidCarry: 0,
  overkillCarry: 0,
  basicDoubled: false,
  nullHeld: 0,
  spells: [],
};

/** Lancer vide, renvoyé par `rollThrow()` hors combat. */
const EMPTY_THROW: ThrowResult = {
  id: -1,
  dice: [],
  total: 0,
  spells: [],
  spell: null,
  spellSeconds: 0,
  spellGoldMult: 1,
  spellEvents: [],
  crit: false,
  critMult: 1,
  spellMult: 1,
  subtotal: 0,
  flatBonus: 0,
  byMinion: false,
  critStreak: 0,
  spellStreak: 0,
  hits: 1,
  breakdown: EMPTY_BREAKDOWN,
};

function emptyApply(): ApplyResult {
  return {
    damageDealt: 0,
    killed: false,
    goldGained: 0,
    secondsGained: 0,
    enemyRevived: false,
    dodged: false,
    expeditionLost: false,
    mutilated: false,
    nextEnemy: null,
    hits: 0,
    hitDamages: [],
    damageHeld: 0,
    temporalSeconds: 0,
  };
}

/**
 * *42* (v0.49.0, A98-2) : identifiants du premier dé Maître qui a montré un 4
 * et du premier qui a montré un 2 — tirages **d'origine** seulement (rebond,
 * fantôme et copies ne comptent pas ; un dé annulé non plus), dédoublonnés.
 */
function fortyTwoDice(r: ThrowResult): string[] {
  const out: string[] = [];
  for (const face of [4, 2]) {
    const d = r.dice.find((x) => x.isMaster && x.dieId && !isExtraRoll(x) && !x.ghost && !x.voided && x.face === face);
    if (d?.dieId && !out.includes(d.dieId)) out.push(d.dieId);
  }
  return out;
}

/**
 * Façade unique du jeu — MVP_DESIGN §8. Aucune dépendance DOM : utilisable en
 * headless (tests Vitest, `tools/sim.ts`).
 *
 * `state` expose l'état interne **en lecture seule** : les consommateurs
 * (UI, vue de combat) ne doivent jamais le muter ; ils passent par les méthodes
 * et réagissent aux événements.
 */
export class Game {
  private st: GameState;
  private rng: Rng;
  private listeners = new Map<keyof GameEvents, Set<Listener>>();
  private throwSeq = 0;
  /** Lancers tirés et pas encore appliqués (dés « en vol »). */
  private inFlight = new Map<number, ThrowResult>();
  /** Fenêtre de grâce restante après la fin d'un combat. */
  private grace = 0;
  private timerAccum = 0;
  private derivedCache: DerivedStats | null = null;
  /** Surcharges d'équilibrage (debug) : jamais sauvegardées. */
  private overrides: BalanceOverrides = {};
  private balanceCache: ResolvedBalance | null = null;
  /** File d'ennemis de l'Invasion (4 ennemis + boss). */
  private queue: EnemyState[] = [];
  /**
   * *Machine Critique* : secondes restantes de réduction du temps de retour
   * après un critique. Non cumulable — le chrono se **relance** à chaque
   * critique (§D.18). Transitoire : jamais sauvegardé.
   */
  private critBoost = 0;
  /**
   * Issue du Gobelin Ingénieur mort pendant l'application en cours, rendue par
   * `ApplyResult.engineer`. Transitoire : posée par `resolveDeathEffects`,
   * consommée (et remise à `null`) par `applyThrow`.
   */
  private pendingEngineer: 'time' | 'hp' | 'none' | null = null;
  /**
   * Meilleur niveau atteint dont les portes de l'Abîme ont déjà été annoncées
   * (événement `abyss`, A94-3). Transitoire : jamais sauvegardé ; posé au
   * meilleur niveau de la partie chargée.
   */
  private abyssAnnounced = 1;
  /**
   * Gros Ver des Sables (effet `timeTick`, v0.49.0) : secondes écoulées depuis
   * le dernier « tic » de l'ennemi `timeTickEnemy`. Transitoire.
   */
  private timeTickClock = 0;
  private timeTickEnemy: string | null = null;

  constructor(save?: SaveData, seed?: number) {
    // Une sauvegarde rejetée (version < 2 — A8) repart d'une partie neuve.
    const data = (save ? normalizeSave(save) : null) ?? emptySave(seed ?? randomSeed());
    const rngSeed = seed ?? data.rngSeed.seed;
    this.rng = new Rng(rngSeed, seed !== undefined ? seed : data.rngSeed.state);

    const inventory = data.inventory.slice();
    let equipped = data.equipped.slice();
    if (inventory.length === 0) {
      // Nouvelle partie : « Dé de l'aïeul » (d4 Commun, un sort commun) en
      // emplacement 0 — COMMENTS v0.4.1 §1.3 / A5.
      const starter = createStarterDie(this.rng);
      inventory.push(starter);
      equipped = [starter.id];
    }
    const basics = container.containerDice(data.tree);

    this.st = {
      phase: data.run.phase,
      level: data.run.level,
      day: data.run.day,
      wave: waveOf(data.run.level, data.run.day),
      gold: data.meta.gold,
      pendingGold: 0,
      runGold: data.meta.runGold,
      timeRemaining: 0,
      timeTotal: 0,
      timeSpent: 0,
      timeMax: 0,
      cooldownRemaining: 0,
      stunRemaining: 0,
      spawnDelay: 0,
      enemy: null,
      enemyIndex: 0,
      enemiesTotal: 0,
      inventory,
      equipped,
      nodes: data.tree,
      stats: { ...emptyStats(), ...data.stats },
      combat: {
        kills: 0,
        gold: 0,
        bestThrow: 0,
        throws: [],
        kind: 'expedition',
        durationPlayed: 0,
        inferno: false,
        primesQueue: [],
        timeSpeed: 1,
        nextEnemyHpMult: 1,
        lost: false,
        abandoned: false,
        critStreak: 0,
        spellStreak: 0,
        secondsGained: 0,
        enemiesSpawned: 0,
        woodUsed: false,
        overkillCarry: 0,
        lastTotal: 0,
        lastBasicSum: 0,
        lastMasterFaces: [],
        fortyTwoCopies: [],
        stormRemaining: 0,
        lastMaxFace: 0,
      },
      lastRun: null,
      lastThrow: null,
      invasionReady: false,
      world: data.run.world ?? null,
      bossId: data.run.bossId ?? null,
      minionEnabled: data.meta.minionEnabled === true,
      saveId: data.meta.saveId,
      tainted: data.meta.tainted === true,
      backhoe: data.run.backhoe ?? 0,
      backhoeArmed: data.run.backhoeArmed === true,
      // A112 : fermé par défaut — sans interface qui l'ouvre, le jeu s'arrête
      // au niveau 2. Jamais relu de la sauvegarde.
      entitled: false,
      gateReached: data.run.gateReached === true,
      guinsoo: { ...(data.run.guinsoo ?? {}) },
      container: basics,
      // A94-13 : compteur permanent des Corrupted GachaDice.
      corruptedPurchases: { ...(data.meta.corruptedPurchases ?? {}) },
      // A94-7 : une Relance en attente n'est jamais sauvegardée (« garder »).
      forgePending: null,
    };
    this.syncSlots();
    this.abyssAnnounced = this.st.stats.bestLevel;
    // A94-2 : une partie déjà en mode infini (crédits vus) reçoit *Artisan
    // des dés* au chargement ; une victoire différée le reçoit à son
    // acquittement (`phases.acknowledgeVictory`).
    if (isEndless(this.st.stats.bestLevel) && this.st.stats.creditsSeen) this.grantArtisan();

    // Mode infini (phase 5, A82) : une partie chargée au niveau 11 ou plus sans
    // monde en reçoit un (sauvegardes antérieures à la v0.38.0, ou monde
    // illisible). Puis **victoire différée** : une partie déjà au-delà du
    // Tricorne qui n'a jamais vu les crédits retrouve l'écran de victoire à sa
    // reprise en Accalmie (« Continuer » de l'écran titre navigue vers
    // `state.phase`). Une partie en `runLost` reste en `runLost`.
    phases.ensureWorld({ state: this.st, rng: this.rng });
    if (isEndless(this.st.level) && !this.st.stats.creditsSeen && this.st.phase === 'accalmie') {
      this.st.phase = 'victory';
    }
  }

  static fromSave(s: SaveData): Game {
    return new Game(s);
  }

  /** État interne, en lecture seule. Ne jamais muter depuis l'extérieur. */
  get state(): Readonly<GameState> {
    return this.st;
  }

  // ───────────────────────────────────────────────────────────── Événements

  on<K extends keyof GameEvents>(ev: K, fn: (p: GameEvents[K]) => void): () => void {
    let set = this.listeners.get(ev);
    if (!set) {
      set = new Set();
      this.listeners.set(ev, set);
    }
    set.add(fn as Listener);
    return () => {
      set?.delete(fn as Listener);
    };
  }

  private emit = <K extends keyof GameEvents>(ev: K, payload: GameEvents[K]): void => {
    const set = this.listeners.get(ev);
    if (!set) return;
    for (const fn of Array.from(set)) (fn as (p: GameEvents[K]) => void)(payload);
  };

  private setPhase = (phase: Phase): void => {
    if (this.st.phase === phase) return;
    this.st.phase = phase;
    this.emit('phase', { phase });
  };

  /** Contexte passé aux transitions de phase (`core/phases.ts`). */
  private ctx(): PhaseCtx {
    return {
      state: this.st,
      rng: this.rng,
      stats: this.getDerivedStats(),
      emit: this.emit,
      balance: this.balance(),
      setPhase: this.setPhase,
      resetFlight: () => {
        this.inFlight.clear();
        this.grace = 0;
      },
      openGrace: () => {
        this.grace = this.inFlight.size > 0 ? BALANCE.inFlightGrace : 0;
        if (this.grace === 0) this.inFlight.clear();
      },
      queue: this.queue,
      prepareEnemy: (enemy) => this.prepareEnemy(enemy),
    };
  }

  // ─────────────────────────────────────────────────────────── Stats et dés

  getDerivedStats(): DerivedStats {
    if (!this.derivedCache) {
      this.derivedCache = getDerivedStats(this.st.nodes, this.balance());
    }
    return this.derivedCache;
  }

  /**
   * Bonus **actuel** et bonus du **niveau suivant** d'un nœud, en français —
   * COMMENTS v0.4.1 §2 (info-bulles de l'arbre). Pur, sans effet de bord.
   */
  describeNodeLevel(id: string, level = this.st.nodes[id] ?? 0): NodeLevelText {
    return describeNodeLevel(id, level);
  }

  /**
   * Espérance **analytique** des dégâts d'un lancer avec l'équipement et les
   * arbres actuels — calculateur du panneau de debug (COMMENTS §7) et affichage
   * du palier de dégâts (COMMENTS v0.3.0 §2). Aucune simulation, aucun `Rng`,
   * aucun effet de bord : voir `core/expected.ts` pour les hypothèses.
   */
  expectedThrow(): ExpectedThrow {
    return expectedThrow(this.equippedDice(), this.st.container, this.getDerivedStats(), {
      rearguardDieIds: this.rearguardSlots().map((slot) => this.st.equipped[slot] as string),
      // *Préparation* (v0.50.0, A100-2) : dés des emplacements 1 à 3.
      prepDieIds: this.prepSlots().map((slot) => this.st.equipped[slot] as string),
      // *Caoutchouc* (v0.47.0) : les dés qui rebondissent comptent deux fois.
      bounceDieIds: this.bounceDieIds(this.equippedDice()),
    });
  }

  // ─────────────────────────────────────────── Matériaux, Poker, Grimoires

  /** Bonus de Matériau actifs (dés **équipés**, nœuds acquis) — A95-1. */
  private activeBonuses(): ActiveMaterialBonus[] {
    return activeMaterialBonuses(this.equippedDice(), this.st.nodes);
  }

  /**
   * Bonus de **Matériau** actifs (v0.47.0, A95) : Matériau, nom et palier (3
   * ou 5 pièces), dans l'ordre du catalogue. Lus à chaque appel : changer de
   * dés suffit.
   */
  materialBonuses(): { material: MaterialId; name: string; tier: 3 | 5 }[] {
    return this.activeBonuses().map((b) => ({
      material: b.material,
      name: MATERIAL_BY_ID[b.material].name,
      tier: b.tier,
    }));
  }

  /** Sorts d'emplacement débloqués — ceux du *Poker* compris si `aby_poker` est acquis. */
  slotSpellsUnlocked(): SlotSpellId[] {
    return this.getDerivedStats().unlockedSpells.slice();
  }

  /** Niveaux des deux Grimoires (A94-11) : *Grimoire des nombres* et *Face je gagne*. */
  grimoireLevels(): { numbers: number; faces: number } {
    const stats = this.getDerivedStats();
    return { numbers: stats.grimoireNumbers, faces: stats.grimoireFaces };
  }

  /**
   * Condition et effet d'un sort, **Grimoires appliqués** (conditions élargies,
   * multiplicateurs renforcés) — `core/spellText.ts`.
   */
  spellDescription(id: SlotSpellId | DieSpellId): SpellText {
    const stats = this.getDerivedStats();
    return describeSpell(id, {
      grimoireNumbers: stats.grimoireNumbers,
      grimoireFaces: stats.grimoireFaces,
      slots: stats.slots,
    });
  }

  /**
   * *Caoutchouc* : dés qui rebondissent parmi `active` — ceux des emplacements
   * 1 (3 pièces) ou 1 à 3 (5 pièces), s'ils sont lancés.
   */
  private bounceDieIds(active: readonly MasterDie[], bonuses = this.activeBonuses()): string[] {
    const n = rubberSlots(bonuses);
    if (n <= 0) return [];
    const out: string[] = [];
    for (let slot = 0; slot < Math.min(n, this.st.equipped.length); slot++) {
      const id = this.st.equipped[slot];
      if (id && active.some((d) => d.id === id)) out.push(id);
    }
    return out;
  }

  /**
   * Prépare un ennemi qui apparaît : *Du Vide 3* (A95-2) neutralise son effet
   * (boss compris) avant l'événement `spawn`.
   */
  private prepareEnemy(enemy: EnemyState, bonuses = this.activeBonuses()): void {
    if (hasMaterialBonus(bonuses, 'void', 3)) voidEnemyEffect(enemy);
  }

  /**
   * Mémoire du lancer appliqué (*Boisé 5*, *Silicium 5*, *Du Vide 5*) : somme
   * propre des Basiques, faces Maîtres (absorbées et fantôme exclues), total
   * propre. Vit dans `state.combat`, jamais sauvegardée.
   */
  private rememberThrow(r: ThrowResult): void {
    const c = this.st.combat;
    c.lastTotal = ownTotal(r);
    c.lastBasicSum = ownBasicSum(r.breakdown);
    c.lastMasterFaces = r.dice
      .filter((d) => d.isMaster && !d.absorbed && !d.ghost)
      .map((d) => ({ face: d.face, sides: d.sides }));
    // *Rouleau Compresseur* (A98-4) : plus haute face des sorts de ce lancer
    // (copies et fantôme compris, dés annulés exclus).
    let max = 0;
    for (const d of r.dice) if (d.isMaster && !d.voided && d.face > max) max = d.face;
    c.lastMaxFace = max;
  }

  /**
   * PV d'un type d'ennemi au niveau `level`, jour `day` (Invasion : jour 6) —
   * surcharges d'équilibrage de debug comprises. Permet à l'interface d'annoncer
   * les PV du boss de la prochaine Invasion.
   */
  enemyHpAt(level: number, day: number, typeId: string): number {
    return enemyHpAt(level, day, typeId, this.balance());
  }

  /** Réglages d'équilibrage résolus (constantes + surcharges de debug). */
  private balance(): ResolvedBalance {
    if (!this.balanceCache) this.balanceCache = resolveBalance(this.overrides);
    return this.balanceCache;
  }

  private invalidateStats(): void {
    this.derivedCache = null;
    this.balanceCache = null;
    this.syncContainer();
    this.syncSlots();
  }

  /** Recalcule le contenant depuis l'arbre de talents — COMMENTS v0.4.1 §1.2. */
  private syncContainer(): void {
    this.st.container = container.containerDice(this.st.nodes);
  }

  // ──────────────────────────────────────── Surcharges d'équilibrage (debug)

  /**
   * Fusionne des surcharges d'équilibrage — COMMENTS §7.
   * Elles ne sont **pas** sauvegardées et s'appliquent au prochain calcul
   * concerné (PV des ennemis à l'apparition, durée au démarrage d'un combat,
   * multiplicateurs au prochain lancer). `setOverrides({})` ne change rien.
   */
  setOverrides(o: BalanceOverrides): void {
    if (!o || typeof o !== 'object') return;
    const next: BalanceOverrides = { ...this.overrides };
    for (const [key, value] of Object.entries(o)) {
      if (typeof value === 'number' && Number.isFinite(value)) {
        (next as Record<string, number>)[key] = value;
      }
    }
    // A87 : une surcharge posée marque la partie d'elle-même (exclue des classements).
    if (Object.keys(next).length > 0) this.markTainted();
    this.overrides = next;
    this.invalidateStats();
  }

  /**
   * Marque la partie comme **truquée** (A87) : à appeler pour toute action de
   * debug qui donne quelque chose ou change l'équilibrage. Collant jusqu'à la
   * prochaine « Nouvelle partie » ; persisté. Les classements excluent les
   * combats d'une partie marquée.
   */
  markTainted(): void {
    this.st.tainted = true;
  }

  /** La partie est-elle marquée (A87) ? */
  isTainted(): boolean {
    return this.st.tainted === true;
  }

  /** Efface toutes les surcharges (retour aux valeurs de `src/data/balance.ts`). */
  clearOverrides(): void {
    this.overrides = {};
    this.invalidateStats();
  }

  /** Copie des surcharges actives (objet vide = aucune). */
  getOverrides(): BalanceOverrides {
    return { ...this.overrides };
  }

  private syncSlots(): void {
    shop.syncSlots(this.st, this.getDerivedStats().slots);
  }

  /** Dés Maîtres équipés, dans l'ordre des emplacements (emplacements vides ignorés). */
  equippedDice(): MasterDie[] {
    const out: MasterDie[] = [];
    for (const id of this.st.equipped) {
      if (!id) continue;
      const die = this.st.inventory.find((d) => d.id === id);
      if (die) out.push(die);
    }
    return out;
  }

  /**
   * **Emplacements bloqués** par l'ennemi courant (Orc Mage 1 — A88 ; la
   * Licorne sadique annule deux dés au hasard depuis v0.50.0) — 0 s'il n'y en
   * a pas ou si l'ennemi est mort. Les dés de ces
   * emplacements sont **retirés du lancer** tant que l'ennemi vit (§F) : ni
   * face, ni dégât plat, ni participation aux sorts. Le HUD les grise.
   */
  blockedSlots(): number {
    const enemy = this.st.enemy;
    const block = findEffect(enemy, 'blockSlots');
    if (!enemy || enemy.hp <= 0 || !block) return 0;
    return Math.max(0, Math.min(block.slots, this.st.equipped.length));
  }

  /**
   * Dés Maîtres réellement lancés : les emplacements bloqués par l'ennemi sont
   * écartés (les emplacements vides ne comptent pas, ils n'avaient pas de dé).
   */
  private activeDice(): MasterDie[] {
    const blocked = this.blockedSlots();
    if (blocked <= 0) return this.equippedDice();
    const out: MasterDie[] = [];
    this.st.equipped.forEach((id, slot) => {
      if (!id || slot < blocked) return;
      const die = this.st.inventory.find((d) => d.id === id);
      if (die) out.push(die);
    });
    return out;
  }

  /**
   * *Arrière-garde* (A89-6) : indices des **deux emplacements équipés les plus
   * hauts** (un seul s'il n'y a qu'un dé équipé), par ordre croissant ; `[]`
   * sans le talent ou sans dé équipé.
   */
  rearguardSlots(): number[] {
    if (!(this.getDerivedStats().rearguardBoost > 0)) return [];
    const out: number[] = [];
    for (let slot = this.st.equipped.length - 1; slot >= 0 && out.length < 2; slot--) {
      const id = this.st.equipped[slot];
      if (id && this.st.inventory.some((d) => d.id === id)) out.unshift(slot);
    }
    return out;
  }

  /**
   * *Préparation* (v0.50.0, COMMENTS v0.49.1 §1.3, A100-2) : indices des
   * emplacements **1 à 3** (0 à 2) qui portent un dé Maître, par ordre
   * croissant ; `[]` sans le talent ou sans dé équipé. À l'image de
   * `rearguardSlots` : un dé à la fois parmi ces trois et parmi les deux
   * derniers cumule les deux renforts.
   */
  prepSlots(): number[] {
    if (!(this.getDerivedStats().prepBoost > 0)) return [];
    const out: number[] = [];
    const last = Math.min(BALANCE.nodePrepSlots, this.st.equipped.length);
    for (let slot = 0; slot < last; slot++) {
      const id = this.st.equipped[slot];
      if (id && this.st.inventory.some((d) => d.id === id)) out.push(slot);
    }
    return out;
  }

  /** Le joueur est-il **assommé** ? (A29 — aucun lancer possible) */
  isStunned(): boolean {
    return this.st.stunRemaining > 0;
  }

  private isCombat(): boolean {
    return this.st.phase === 'expedition' || this.st.phase === 'invasion';
  }

  // ───────────────────────────────────────────────────────────────── Lancer

  /**
   * Un lancer est-il possible **maintenant** ? (combat en cours, chrono et
   * temps de retour écoulés, ennemi vivant)
   */
  private throwReady(): boolean {
    const s = this.st;
    if (!this.isCombat()) return false;
    if (s.timeRemaining <= 0) return false;
    // Assommé (A29) : aucun lancer, ni manuel, ni automatique, ni du *Larbin*.
    if (s.stunRemaining > 0) return false;
    if (s.cooldownRemaining > 0 || s.spawnDelay > 0) return false;
    // Orc Mage : tous les dés Maîtres bloqués et pas de contenant — il n'y a
    // rien à lancer, le lancer ne se déclenche pas (§F).
    if (this.blockedSlots() > 0 && s.container.length === 0 && this.activeDice().length === 0) {
      return false;
    }
    return s.enemy !== null && s.enemy.hp > 0;
  }

  /**
   * Le **joueur** peut-il lancer ? Faux tant que le *Larbin* est actif : ses
   * lancers **remplacent** les lancers manuels (A18, « un seul flux »).
   */
  canThrowManual(): boolean {
    return this.throwReady() && !this.minionActive();
  }

  /** Le *Larbin* peut-il lancer ? (débloqué, activé, et lancer possible) */
  canThrowMinion(): boolean {
    return this.minionActive() && this.throwReady();
  }

  /** Lancer manuel — c'est `canThrowManual()` : la vue n'a rien à changer. */
  canThrow(): boolean {
    return this.canThrowManual();
  }

  throwCooldownRemaining(): number {
    return Math.max(0, this.st.cooldownRemaining);
  }

  /** Le *Larbin* est-il débloqué par l'arbre (`arc_minion`) ? */
  hasMinion(): boolean {
    return this.getDerivedStats().minionUnlocked;
  }

  /** Le *Larbin* est-il débloqué **et** activé ? */
  minionActive(): boolean {
    return this.st.minionEnabled && this.hasMinion();
  }

  /**
   * Active ou désactive le *Larbin* (A18). Sans le nœud `arc_minion`, l'appel
   * est refusé (`false`). L'état est **persisté**.
   */
  setMinionEnabled(on: boolean): boolean {
    if (on && !this.hasMinion()) return false;
    this.st.minionEnabled = on === true;
    return true;
  }

  /**
   * Marque le lore d'ouverture comme déjà montré (A33, COMMENTS v0.8.0 §1.1).
   * Idempotent, appelé une fois à la fermeture de l'encart de lore. L'état est
   * **persisté** (`stats.loreSeen`, champ additif — `core/save.ts`).
   */
  markLoreSeen(): void {
    this.st.stats.loreSeen = true;
  }

  /** Monde du niveau courant (campagne : la zone du niveau) — phase 5. */
  world(): WorldRef {
    return worldOf(this.st);
  }

  /** Nom du monde courant (« Terres mêlées » pour un monde procédural). */
  worldName(): string {
    return worldNameOf(worldOf(this.st));
  }

  /** Boss de la prochaine Invasion (campagne : celui de la zone ; infini : tiré). */
  currentBossId(): EnemyTypeId {
    return currentBossId(this.st);
  }

  /** Le niveau courant est-il en mode infini (≥ 11) ? */
  isEndless(): boolean {
    return isEndless(this.st.level);
  }

  /**
   * Temps de retour du *Larbin* : `temps de retour effectif × 1,25 ×
   * (1 − 0,05 × Coup de fouet)` — A18. « Effectif » inclut la réduction
   * temporaire de *Machine Critique*, comme pour le joueur.
   */
  minionCooldown(): number {
    const stats = this.getDerivedStats();
    return stats.minionCooldown * this.critMachineFactor() * this.slowThrowFactor() * this.stormFactor();
  }

  /**
   * *Tempête* (v0.49.0, A98-4) : secondes restantes de tempête (5 s au
   * déclenchement, rafraîchies, non cumulées), 0 sans tempête — HUD.
   */
  stormRemaining(): number {
    return Math.max(0, this.st.combat.stormRemaining ?? 0);
  }

  /**
   * *Tempête* : facteur du temps de retour du joueur **et** du *Larbin*
   * pendant la tempête — `1 − 0,25 × (1 + 0,1 × Grimoire des nombres)` (A98-8),
   * 1 sans tempête. Composé avec *Machine Critique* et l'Araignée.
   */
  stormFactor(): number {
    if (!(this.stormRemaining() > 0)) return 1;
    const reduction = BALANCE.spellStormReduction * grimoireFactor(this.getDerivedStats().grimoireNumbers);
    return 1 - Math.min(0.95, Math.max(0, reduction));
  }

  /**
   * **Araignée** (§F) : « réduit la vitesse de lancer de 15 % » se lit comme un
   * temps de retour **divisé par 0,85** (vitesse ×0,85 ⇔ période ×1,176), pour
   * le joueur comme pour le *Larbin*, tant que l'Araignée vit. v0.49.0 : la
   * Veuve Noire aussi (COMMENTS v0.48.1 §1.8).
   */
  private slowThrowFactor(): number {
    const enemy = this.st.enemy;
    const slow = findEffect(enemy, 'slowThrow');
    if (!enemy || enemy.hp <= 0 || !slow) return 1;
    const percent = Math.min(0.95, Math.max(0, slow.percent));
    return 1 / (1 - percent);
  }

  /**
   * Temps de retour qu'appliquerait un lancer **du joueur** maintenant :
   * *Machine Critique* et ralentissement de l'Araignée compris. (Le temps
   * restant du temps de retour en cours, lui, est `throwCooldownRemaining()`.)
   */
  throwCooldownNow(): number {
    return this.playerCooldown();
  }

  /**
   * Facteur de temps de retour de *Machine Critique* : `1 − 0,05 × niveau`
   * pendant les 5 s qui suivent un critique, 1 sinon (§D.18). Public depuis
   * A88 (indicateur du HUD).
   */
  critMachineFactor(): number {
    return this.critBoost > 0 ? this.getDerivedStats().critCooldownFactor : 1;
  }

  /**
   * Secondes restantes de *Machine Critique* (A88, indicateur du HUD) : de 5
   * à 0 après un critique, 0 si la réduction est inactive ou sans le talent.
   * S'écoule dans `tick()` (événement `timer`), se relance au tirage d'un
   * critique (événement `throw`).
   */
  critMachineRemaining(): number {
    if (this.getDerivedStats().critCooldownFactor >= 1) return 0;
    return Math.max(0, this.critBoost);
  }

  /**
   * Multiplicateurs de série (*Criticité*, *Flux arcanique*) qui
   * s'appliqueront au **prochain** lancer s'il prolonge la série en cours
   * (`state.combat.critStreak` / `spellStreak` + 1) — 1 sans le talent, et 1
   * tant que la série en cours est vide (le 1er coup d'une série n'est pas
   * multiplié). Indicateur du HUD (A88), mis à jour à chaque `throw`.
   */
  streakMultipliers(): { crit: number; spell: number } {
    const stats = this.getDerivedStats();
    const c = this.st.combat;
    return {
      crit: streakMultiplier(stats.critStreakFactor ?? 1, c.critStreak + 1),
      spell: streakMultiplier(stats.spellStreakFactor ?? 1, c.spellStreak + 1),
    };
  }

  /**
   * Plafond d'ennemis de l'Expédition (A88) : `BALANCE.expeditionEnemyCap`
   * (50) pendant une Expédition et sur son écran de fin, **0** ailleurs
   * (Invasion, Accalmie…). Le HUD affiche `state.combat.kills / cap`.
   */
  expeditionEnemyCap(): number {
    const phase = this.st.phase;
    return phase === 'expedition' || phase === 'expeditionEnd' ? BALANCE.expeditionEnemyCap : 0;
  }

  /** Temps de retour appliqué au prochain lancer du joueur. */
  private playerCooldown(): number {
    return (
      this.getDerivedStats().throwCooldown *
      this.critMachineFactor() *
      this.slowThrowFactor() *
      this.stormFactor()
    );
  }

  /**
   * Tire les faces de tous les dés et calcule le résultat.
   * N'applique aucun dégât : seul le cooldown démarre.
   * Hors combat (ou pendant le cooldown) renvoie un lancer vide (`id: -1`).
   *
   * `opts.byMinion` : lancer du *Larbin* (dégâts ×0,75, temps de retour du
   * serviteur). Réservé à la boucle interne — l'interface ne l'appelle jamais.
   */
  rollThrow(opts: { byMinion?: boolean } = {}): ThrowResult {
    const byMinion = opts.byMinion === true;
    if (byMinion ? !this.canThrowMinion() : !this.canThrowManual()) return EMPTY_THROW;
    const s = this.st;
    const stats = this.getDerivedStats();

    // Matériaux (v0.47.0) : bonus lus à ce lancer ; *Du Vide 3* neutralise un
    // ennemi apparu avant que le bonus ne s'allume (dés changés en combat).
    const bonuses = this.activeBonuses();
    if (s.enemy) this.prepareEnemy(s.enemy, bonuses);

    // *Premiers* (A17) : le multiplicateur en tête de file est consommé par ce
    // lancer. `inSequence` retient qu'une séquence courait **avant** lui : un
    // redéclenchement pendant la séquence est ignoré.
    const inSequence = s.combat.primesQueue.length > 0;
    const primesMult = inSequence ? (s.combat.primesQueue.shift() ?? 1) : 1;

    // Orc Mage : les dés des emplacements bloqués ne sont pas lancés du tout
    // (§F) — `activeDice()` les a déjà écartés.
    const blockedSlots = this.blockedSlots();
    const equipped = this.equippedDice();
    const active = this.activeDice();

    // *Dé enragé de Guinsoo* (A72-15) : un compteur par dé, +1 à chaque lancer
    // où il **participe** (dé lancé, emplacement non bloqué) ; au 3e, les
    // dégâts du lancer seront appliqués deux fois et le compteur repart à 0.
    let hits = 1;
    for (const die of active) {
      if (!hasAffix(die, 'guinsoo')) continue;
      const count = (s.guinsoo[die.id] ?? 0) + 1;
      if (count >= 3) {
        hits = 2;
        delete s.guinsoo[die.id];
      } else {
        s.guinsoo[die.id] = count;
      }
    }

    // *Dé du chef de chantier* (A72-17) : le lancer **armé** emporte les dés
    // du tractopelle, qui se vide ; tout autre lancer (manuel, auto, Larbin)
    // le recharge de la taille du contenant tant qu'un tel dé est équipé.
    const foreman = equipped.some((d) => hasAffix(d, 'foreman'));
    let backhoe = 0;
    if (foreman && s.backhoeArmed && s.backhoe > 0) {
      backhoe = s.backhoe;
      s.backhoe = 0;
      s.backhoeArmed = false;
    } else if (foreman && s.container.length > 0) {
      s.backhoe += s.container.length;
    }

    const bounceIds = this.bounceDieIds(active, bonuses);

    /**
     * *42* (A98-2) : les dés mémorisés au lancer déclencheur sont lancés une
     * fois de plus **à ce lancer**, s'ils y participent (un dé déséquipé ou
     * bloqué entre-temps n'est pas copié) ; la liste est vidée.
     */
    const fortyTwoCopies = s.combat.fortyTwoCopies.filter((dieId) => active.some((d) => d.id === dieId));
    s.combat.fortyTwoCopies = [];

    const throwOpts: ThrowOptions = {
      byMinion,
      inferno: s.combat.inferno,
      primesMult,
      blockedSlots,
      // *Dice Master* (A72-5) : moyenne des raretés des Maîtres **équipés**.
      diceMasterMult: stats.diceMaster ? diceMasterMultiplier(equipped) : 1,
      critStreak: s.combat.critStreak,
      spellStreak: s.combat.spellStreak,
      archidicer: equipped.some((d) => hasAffix(d, 'archidicer')),
      backhoe,
      hits,
      // *Arrière-garde* (A89-6) : dés des deux derniers emplacements équipés.
      rearguardDieIds: this.rearguardSlots().map((slot) => this.st.equipped[slot] as string),
      // *Préparation* (v0.50.0, A100-2) : dés des emplacements 1 à 3.
      prepDieIds: this.prepSlots().map((slot) => this.st.equipped[slot] as string),
      materials: {
        bonuses,
        lastBasicSum: s.combat.lastBasicSum,
        lastMasterFaces: s.combat.lastMasterFaces,
        lastTotal: s.combat.lastTotal,
        overkillCarry: s.combat.overkillCarry,
        broken: s.enemy?.broken ?? 0,
        bounceDieIds: bounceIds,
      },
      fortyTwoCopies,
      lastMaxFace: s.combat.lastMaxFace,
    };
    const id = ++this.throwSeq;
    let result = buildThrow(id, active, stats, s.enemy, this.rng, s.container, throwOpts);
    // *Triche* (A72-6) : un second lancer complet est tiré (le `Rng` est
    // consommé deux fois) ; celui au **total de dégâts** le plus élevé est
    // gardé (le premier à égalité), seuls ses sorts et événements comptent.
    if (stats.cheat) {
      const second = buildThrow(id, active, stats, s.enemy, this.rng, s.container, throwOpts);
      if (second.total > result.total) {
        second.cheat = { kept: 2, discardedTotal: result.total };
        result = second;
      } else {
        result.cheat = { kept: 1, discardedTotal: second.total };
      }
    }
    // Séries de *Criticité* et de *Flux arcanique* (A72-3, 4) : au niveau du
    // combat (A77, `state.combat` depuis A88), mises à jour au tirage comme la
    // file de *Premiers*.
    s.combat.critStreak = result.critStreak;
    s.combat.spellStreak = result.spellStreak;
    if (foreman || backhoe > 0) this.emit('backhoe', { count: s.backhoe, armed: s.backhoeArmed });

    // *666* : l'Enfer s'allume **après** le lancer déclencheur, qui n'en profite
    // pas ; il brûle ensuite jusqu'à la fin du combat. Non cumulable.
    if (!s.combat.inferno && result.spells.some((sp) => sp.id === 'sixes')) {
      s.combat.inferno = true;
      result.spellEvents.push({ id: 'sixes', kind: 'inferno' });
    }
    // *Premiers* : la séquence ×2, ×3, ×5 démarre au lancer **suivant**.
    if (!inSequence && result.spells.some((sp) => sp.id === 'primes')) {
      s.combat.primesQueue = BALANCE.spellPrimesSequence.slice();
      result.spellEvents.push({ id: 'primes', kind: 'primes' });
    }

    // *42* (A98-2) : le lancer déclencheur mémorise le dé qui a montré le 4 et
    // celui qui a montré le 2 (tirages d'origine seulement), copiés au suivant.
    if (result.spells.some((sp) => sp.id === 'fortyTwo')) {
      s.combat.fortyTwoCopies = fortyTwoDice(result);
    }

    s.cooldownRemaining = byMinion ? this.minionCooldown() : this.playerCooldown();
    // *Machine Critique* : le chrono de réduction se relance à chaque critique.
    if (result.crit && stats.critCooldownFactor < 1) this.critBoost = BALANCE.critMachineDuration;
    // *Tempête* (A98-4) : 5 s, rafraîchies, non cumulées — le lancer
    // déclencheur garde son temps de retour, comme *Machine Critique*.
    if (result.spells.some((sp) => sp.id === 'storm')) {
      s.combat.stormRemaining = BALANCE.spellStormDuration;
    }
    // *Silicium 3* (A95-3) : l'excédent reporté est consommé par ce lancer.
    if (result.breakdown.overkillCarry > 0) s.combat.overkillCarry = 0;

    s.lastThrow = result;
    s.stats.throws += 1;
    this.inFlight.set(result.id, result);
    this.emit('throw', result);
    if (bonuses.length > 0) this.emitThrowMaterials(result, bonuses);
    return result;
  }

  /** Puces du HUD (événement `material`) des bonus qui ont agi au tirage. */
  private emitThrowMaterials(r: ThrowResult, bonuses: readonly ActiveMaterialBonus[]): void {
    const b = r.breakdown;
    const bounces = r.dice.filter((d) => d.bounce).length;
    if (bounces > 0) this.emit('material', { material: 'rubber', kind: 'bounce', value: bounces });
    const ghost = r.dice.find((d) => d.ghost);
    if (ghost) this.emit('material', { material: 'silicon', kind: 'ghost', value: ghost.face });
    if (b.woodBasicCarry > 0) {
      this.emit('material', { material: 'wood', kind: 'woodCarry', value: b.woodBasicCarry });
    }
    if (hasMaterialBonus(bonuses, 'iron', 5)) {
      const k = r.dice.filter((d) => d.isMaster && !d.ghost && !d.absorbed && d.face >= d.sides)
        .length;
      if (k > 0) this.emit('material', { material: 'iron', kind: 'ironMax', value: k });
    }
    if (b.voidCarry > 0) {
      this.emit('material', { material: 'void', kind: 'voidCarry', value: b.voidCarry });
    }
  }

  /**
   * Applique un lancer : dégâts, sort, poison, mort, or, secondes gagnées.
   * Accepté pendant le combat et jusqu'à `inFlightGrace` (1,5 s) après sa fin,
   * pour les dés encore en vol quand le chrono atteint 0 (DECISIONS D).
   */
  applyThrow(r: ThrowResult): ApplyResult {
    const s = this.st;
    if (!r || r.id < 0 || !this.inFlight.has(r.id)) return emptyApply();
    this.inFlight.delete(r.id);
    this.pendingEngineer = null;

    const enemy = s.enemy;

    /**
     * **Esquive** (Loup, Squelette Archer, Lapinou garou) — A29 / §F : tirée à
     * l'**application** (les dés retombent, puis l'ennemi esquive) et **avant
     * tout effet**. Le lancer entier est perdu : ni dégâts, ni poison, ni
     * mutilation, ni secondes ou or de ses sorts. Il est journalisé à 0, marqué
     * *Esquivé*, et ne peut pas devenir le « meilleur lancer » du combat.
     */
    const dodge = findEffect(enemy, 'dodge');
    if (enemy && enemy.hp > 0 && dodge && this.rng.chance(dodge.chance)) {
      this.rememberThrow(r);
      const dodgedOut: ApplyResult = { ...emptyApply(), dodged: true };
      this.logThrow(r, enemy, 0, false, true);
      this.emit('apply', { ...dodgedOut, result: r });
      return dodgedOut;
    }

    if (r.total > s.stats.bestThrow) s.stats.bestThrow = r.total;
    if (r.total > s.combat.bestThrow) s.combat.bestThrow = r.total;

    this.rememberThrow(r);

    if (!enemy || enemy.hp <= 0) {
      const out = emptyApply();
      this.logThrow(r, enemy, 0, false);
      this.emit('apply', { ...out, result: r });
      return out;
    }

    /**
     * Marque de *Null* (v0.50.0, COMMENTS v0.49.1 §1.2 — retour à la première
     * lecture ; A98-3 retenait les dégâts). Monstre **marqué** : un lancer sans
     * Null **frappe normalement** (poison, mutilation, Serpent, armure comme
     * d'habitude) **et** son total, `r.total` par coup porté, s'ajoute à la
     * marque ; un lancer avec Null **libère** (le cumul connu au tirage est
     * déjà dans `r.total`, celui ajouté depuis s'y ajoute) et la marque
     * disparaît. Monstre **sans marque** : un lancer avec Null frappe
     * normalement puis pose la marque. Un lancer tiré pour libérer une marque
     * disparue depuis (autre monstre) n'en garde rien. Perdue à la mort.
     */
    const nullTrig = r.spells.some((sp) => sp.id === 'nullMark');
    const mark = enemy.nullMark;
    const armor = r.breakdown.armorDivisor > 0 ? r.breakdown.armorDivisor : 1;
    let extra = 0;
    let accumulate = false;
    let released: number | null = null;
    if (mark) {
      if (nullTrig) {
        released = mark.held;
        extra = Math.max(0, mark.held - (r.nullRelease ?? 0)) / armor;
        enemy.nullMark = null;
      } else {
        accumulate = true;
      }
    } else if (r.nullRelease !== undefined) {
      extra = -(r.breakdown.nullHeld ?? 0) / armor;
    }

    // Poison : une pile par lancer, égale à Σ Empoisonné (plafonné) × dégâts du
    // lancer, par seconde pendant 4 s (COMMENTS §4). Les piles s'accumulent.
    const dps = poisonOf(r, s.inventory, this.balance().poisonCap);
    if (dps > 0) addPoison(enemy, dps);

    // Bouclier : **A26** — il ne se brise plus, il annule le premier dé Maître
    // de chaque lancer tant que l'ennemi vit (l'absorption elle-même est faite
    // au tirage, dans `buildThrow`). `shieldBroken` reste à `false`.

    let secondsGained = r.spellSeconds;
    const limit = findEffect(enemy, 'hitLimit')?.maxHits ?? 0;
    /**
     * Coups du lancer : un d'ordinaire, **deux** au 3e lancer d'un *Guinsoo*
     * (A72-15) — les mêmes dégâts, appliqués deux fois. Chaque coup compte pour
     * la limite du Serpent (A28 : seuls les lancers **appliqués et non
     * esquivés** comptent, pas le poison). Si le premier coup tue, le second
     * n'est pas porté : mort, or et poison ne sont traités qu'une fois. Une
     * résurrection, elle, encaisse le second coup. v0.52.0 (A102) : le Serpent
     * arrivé à sa limite encaisse les coups suivants comme un autre monstre.
     *
     * *Mutilant* : l'ennemi déjà mutilé subit ×1,3 **sur ce lancer** ; la
     * mutilation posée par ce même lancer ne vaut que pour les suivants.
     */
    const wanted = r.hits === 2 ? 2 : 1;
    const hitDamages: number[] = [];
    let killed = false;
    let revived = false;
    let dealt = 0;
    /** *Silicium 3* (A95-3) : excédent du coup fatal (dégâts − PV restants avant ce coup). */
    let overkill = 0;
    let held = 0;
    /** Le Serpent vient d'atteindre sa limite de coups par ce lancer (v0.52.0). */
    let limitReached = false;
    for (let h = 0; h < wanted; h++) {
      enemy.hits += 1;
      if (limit > 0 && enemy.hits === limit) limitReached = true;
      // *Null* : un coup porté contre un monstre marqué verse `r.total` à la marque.
      if (accumulate) held += r.total;
      const amount = Math.max(0, r.total + extra) * mutilationFactor(enemy);
      const hpBefore = enemy.hp;
      const hit = damageEnemy(enemy, amount);
      hitDamages.push(hit.dealt);
      dealt += hit.dealt;
      if (hit.revived) revived = true;
      if (hit.killed) {
        killed = true;
        overkill = Math.max(0, amount - hpBefore);
        break;
      }
    }
    const mats = this.activeBonuses();
    if (killed && overkill > 0 && hasMaterialBonus(mats, 'silicon', 3)) {
      s.combat.overkillCarry += overkill;
      this.emit('material', { material: 'silicon', kind: 'overkill', value: overkill });
    }
    /**
     * *Osseux* (A94-14) : un lancer à sort d'emplacement **brise** l'ennemi
     * qu'il a touché et laissé debout (une résurrection le garde brisé), après
     * application ; *Osseux 5* : chaque nouveau bris ajoute un cran.
     */
    if (
      !killed &&
      enemy.hp > 0 &&
      hasMaterialBonus(mats, 'bone', 3) &&
      r.spells.some((sp) => sp.kind === 'slot')
    ) {
      const before = enemy.broken;
      if (before <= 0) enemy.broken = 1;
      else if (hasMaterialBonus(mats, 'bone', 5)) enemy.broken = before + 1;
      if (enemy.broken !== before) {
        this.emit('material', { material: 'bone', kind: 'broken', value: enemy.broken });
      }
    }
    const mutilated = mutilationRoll(r, s.inventory, this.rng);
    if (mutilated && enemy.hp > 0) applyMutilation(enemy);
    this.afterHitEffects(enemy);
    // *Null* : le cumul ne vaut que si le monstre marqué reste debout (sa mort
    // fait perdre la marque, `handleKill` plus bas).
    if (!(accumulate && mark && !killed && enemy.hp > 0)) held = 0;
    if (accumulate && mark && held > 0) {
      mark.held += held;
      r.breakdown.nullHeld = held;
      this.emit('nullMark', { held: mark.held });
    } else if (released !== null) {
      this.emit('nullMark', { held: 0, released });
    } else if (nullTrig && !killed && enemy.hp > 0 && s.enemy === enemy) {
      enemy.nullMark = { held: 0 };
      this.emit('nullMark', { held: 0 });
    }

    let goldGained = 0;
    let nextEnemy: EnemyState | null = null;
    /** *Temporel* (v0.50.0) : secondes du lancer, lues à part pour l'horloge de la vue 3D. */
    let temporalSeconds = 0;
    if (killed) {
      const bonuses = killBonuses(r, s.inventory, this.rng);
      temporalSeconds = bonuses.seconds;
      secondsGained += bonuses.seconds;
      goldGained = this.handleKill(enemy, bonuses.goldBonus, r.spellGoldMult);
      nextEnemy = s.enemy;
    }
    if (secondsGained > 0) s.combat.secondsGained += this.addTime(secondsGained);

    /**
     * **Serpent** (v0.52.0, COMMENTS v0.51.0 §1.1, A102) : s'il est encore
     * vivant après son 3e coup, le **temps restant** d'Expédition ou
     * d'Invasion est **divisé par 2**, une seule fois (au lancer qui porte le
     * 3e coup ; un lancer qui le tue n'est pas pénalisé), par `drainTime`
     * (événement `timeDrain`). Le combat continue. Avant (A28), l'Expédition
     * était perdue et l'Invasion vaut défaite : plus aucun monstre ne déclenche
     * `expeditionLost`, conservé à `false` pour la vue et les écrans.
     */
    let timeHalved = 0;
    if (limitReached && !killed && enemy.hp > 0) {
      timeHalved = this.drainTime(s.timeRemaining / 2);
    }
    const expeditionLost = false;

    const out: ApplyResult = {
      damageDealt: dealt,
      killed,
      goldGained,
      secondsGained,
      enemyRevived: revived,
      dodged: false,
      expeditionLost,
      mutilated,
      nextEnemy,
      hits: hitDamages.length,
      hitDamages,
      damageHeld: held,
      temporalSeconds,
    };
    if (timeHalved > 0) out.timeHalved = timeHalved;
    if (this.pendingEngineer) out.engineer = this.pendingEngineer;
    this.pendingEngineer = null;
    this.logThrow(r, enemy, dealt, killed, false, hitDamages.length);
    this.emit('apply', { ...out, result: r });
    // Un événement par coup porté (A72-15) : la vue et le HUD affichent les
    // deux chiffres d'un lancer de *Guinsoo*.
    hitDamages.forEach((damage, i) => {
      this.emit('hit', {
        result: r,
        hit: i + 1,
        damage,
        crit: r.crit,
        killed: killed && i === hitDamages.length - 1,
      });
    });
    if (expeditionLost) {
      this.emit('expeditionLost', { enemy });
      if (s.phase === 'invasion') phases.endInvasion(this.ctx(), false);
      else phases.loseExpedition(this.ctx());
    }
    return out;
  }

  /**
   * Effets de boss **à chaque lancer appliqué et non esquivé** (v0.49.0,
   * COMMENTS v0.48.1 §1.8) :
   *  - **armure** (Super-Méca Gobelin, Tricorne) : un cran de plus, plafonné à
   *    `maxStacks` — il vaudra pour les lancers **suivants** ;
   *  - **Chef des Orcs** (A98-5) : `chance` de vous assommer `seconds`
   *    (événement `stun`), tirée au `Rng` seulement contre lui.
   */
  private afterHitEffects(enemy: EnemyState): void {
    const armor = findEffect(enemy, 'armor');
    if (armor) enemy.armorStacks = Math.min(armor.maxStacks, enemy.armorStacks + 1);
    const stun = findEffect(enemy, 'stunOnHit');
    if (stun && this.rng.chance(stun.chance)) {
      this.st.stunRemaining = stun.seconds;
      this.emit('stun', { seconds: stun.seconds });
    }
  }

  /**
   * Journalise un lancer appliqué — COMMENTS v0.3.0 §2 (écran récapitulatif).
   *
   * Le journal vit dans `state.combat.throws`, n'est **jamais sauvegardé**, et
   * n'est vidé qu'au démarrage du combat suivant (`phases.resetCombat`) : les
   * écrans `expeditionEnd`, `invasionWon` et `runLost` peuvent donc tous le
   * lire après coup.
   */
  private logThrow(
    r: ThrowResult,
    enemy: EnemyState | null,
    damageDealt: number,
    killed: boolean,
    dodged = false,
    hits = damageDealt > 0 ? 1 : 0,
  ): void {
    const s = this.st;
    const entry: CombatThrowEntry = {
      id: r.id,
      at: s.timeSpent,
      enemyName: enemy?.name ?? '—',
      result: r,
      damageDealt,
      killed,
      dodged,
      hits,
    };
    s.combat.throws.push(entry);
    s.combat.durationPlayed = Math.max(s.combat.durationPlayed, s.timeSpent);
  }

  /**
   * Prolonge le chrono (affixe *Temporel*, sort *42*), sans jamais
   * dépasser la durée maximale du combat (BALANCE.maxCombatFactor). Renvoie
   * les secondes **réellement** ajoutées (0 hors combat ou au plafond).
   */
  private addTime(seconds: number): number {
    if (!this.isCombat()) return 0;
    const s = this.st;
    const room = Math.max(0, s.timeMax - s.timeSpent);
    const before = s.timeRemaining;
    s.timeRemaining = Math.min(before + seconds, room);
    s.timeTotal = Math.max(s.timeTotal, s.timeRemaining);
    return Math.max(0, s.timeRemaining - before);
  }

  /** Crédite l'or : en combat il s'accumule, hors combat il part directement au total. */
  private addGold(amount: number): void {
    const s = this.st;
    const value = Math.max(0, Math.round(amount));
    if (value <= 0) return;
    s.combat.gold += value;
    if (this.isCombat()) {
      s.pendingGold += value;
    } else {
      // Fenêtre de grâce : l'or d'une mort hors combat est crédité directement,
      // il fait quand même partie de l'or de la run (DECISIONS Q15).
      s.gold += value;
      s.stats.totalGold += value;
      s.runGold += value;
      this.emit('gold', { gold: s.gold });
    }
  }

  /**
   * Mort d'un ennemi : or, compteurs, apparition du suivant / fin d'Invasion.
   *
   * Ordre des multiplicateurs d'or (§E) : *Butin* (`goldMult`), *Doré*
   * (`goldBonus`), *Nombre d'or* (`spellGoldMult`), puis **Mort dorée** — 1 %
   * par niveau de doubler le tout.
   */
  private handleKill(enemy: EnemyState, goldBonus: number, spellGoldMult = 1): number {
    const s = this.st;
    const stats = this.getDerivedStats();
    let gold = Math.round(enemy.gold * stats.goldMult * (1 + goldBonus) * spellGoldMult);
    if (stats.goldDoubleChance > 0 && this.rng.chance(stats.goldDoubleChance)) gold *= 2;
    this.addGold(gold);
    s.stats.totalKills += 1;
    s.combat.kills += 1;
    enemy.poison = [];
    // *Null* (A98-3) : la marque disparaît avec le monstre, son cumul est perdu.
    if (enemy.nullMark) {
      enemy.nullMark = null;
      this.emit('nullMark', { held: 0 });
    }
    this.emit('kill', { enemy, gold });
    // Effets « à la mort » (Poisson Lune, Gobelin Ingénieur) : résolus **avant**
    // l'apparition du suivant, qui doit pouvoir hériter du ×1,5 PV.
    this.resolveDeathEffects(enemy);

    if (enemy.isBoss) {
      // Le boss peut tomber pendant la fenêtre de grâce : la défaite devient victoire.
      if (s.phase === 'invasion' || s.phase === 'runLost') phases.endInvasion(this.ctx(), true);
      s.enemy = null;
      return gold;
    }

    if (s.phase === 'invasion') {
      s.enemyIndex += 1;
      const next = this.queue.shift() ?? null;
      if (next) this.prepareEnemy(next);
      s.enemy = next;
      if (next) {
        this.consumeHpMult(next);
        s.spawnDelay = BALANCE.spawnDelay;
        s.combat.enemiesSpawned += 1;
        this.emit('spawn', { enemy: next });
      }
      return gold;
    }

    if (s.phase === 'expedition') {
      /**
       * **Plafond d'ennemis** (A88) : le `expeditionEnemyCap`-ième ennemi tué
       * (50) termine l'Expédition aussitôt, **réussie** — même chemin que la
       * fin de chrono (`endExpedition` : or crédité, jour consommé, fenêtre de
       * grâce des dés en vol). Lancer manuel, *Larbin*, poison ou sort : toute
       * mort passe ici. Une résurrection n'est pas une mort (`damageEnemy` ne
       * renvoie « tué » qu'une fois), elle ne compte donc pas deux fois.
       */
      if (s.combat.kills >= BALANCE.expeditionEnemyCap) {
        s.enemy = null;
        phases.endExpedition(this.ctx());
        return gold;
      }
      const next = rollEnemy(s.wave, this.rng, this.balance(), worldOf(s));
      this.prepareEnemy(next);
      this.consumeHpMult(next);
      s.enemy = next;
      s.spawnDelay = BALANCE.spawnDelay;
      s.combat.enemiesSpawned += 1;
      this.emit('spawn', { enemy: next });
      return gold;
    }

    // Hors combat (fenêtre de grâce) : aucun ennemi ne réapparaît.
    s.enemy = null;
    return gold;
  }

  /**
   * Effets déclenchés par la **mort** d'un ennemi (jamais par une résurrection,
   * `damageEnemy` ne renvoyant « tué » qu'une fois) — §F :
   *  - **Poisson Lune** : il explose, le chrono du combat défile ×1,1 jusqu'à
   *    la fin du combat, **cumulable** (deux explosions : ×1,21). Les temps de
   *    retour ne sont pas touchés.
   *  - **Gobelin Ingénieur** : un tirage à trois issues équiprobables — −3 s de
   *    chrono, PV du **prochain** ennemi ×1,5, ou rien. L'issue est rendue à
   *    l'appelant par `ApplyResult.engineer` (`pendingEngineer`), y compris
   *    quand la mort vient du poison — dans ce cas personne ne la lit.
   */
  private resolveDeathEffects(enemy: EnemyState): void {
    const s = this.st;
    const explode = findEffect(enemy, 'explodeFaster');
    if (explode) s.combat.timeSpeed *= explode.factor;
    const effect = findEffect(enemy, 'engineer');
    if (!effect) return;
    const roll = this.rng.int(1, 3);
    const outcome: 'time' | 'hp' | 'none' = roll === 1 ? 'time' : roll === 2 ? 'hp' : 'none';
    if (outcome === 'time') {
      s.timeRemaining = Math.max(0, s.timeRemaining - effect.seconds);
    } else if (outcome === 'hp') {
      // En Invasion, « le prochain » est le suivant de la file, **boss compris**.
      s.combat.nextEnemyHpMult = effect.hpMult;
    }
    this.pendingEngineer = outcome;
  }

  /** Applique (et consomme) le ×1,5 PV laissé par un Gobelin Ingénieur. */
  private consumeHpMult(enemy: EnemyState): void {
    const mult = this.st.combat.nextEnemyHpMult;
    if (mult === 1) return;
    scaleEnemyHp(enemy, mult);
    this.st.combat.nextEnemyHpMult = 1;
  }

  // ───────────────────────────────────────────────────────────────── Boucle

  tick(dt: number): void {
    if (!Number.isFinite(dt) || dt <= 0) return;
    const s = this.st;

    if (!this.isCombat()) {
      if (this.grace > 0) {
        this.grace -= dt;
        if (this.grace <= 0) {
          this.grace = 0;
          this.inFlight.clear();
        }
      }
      return;
    }

    // Chrono de partie (COMMENTS v0.6.0 §2) : le combat compte ici, les menus
    // par `addMenuTime()`.
    s.stats.playSeconds += dt;

    if (s.cooldownRemaining > 0) s.cooldownRemaining = Math.max(0, s.cooldownRemaining - dt);
    if (s.spawnDelay > 0) s.spawnDelay = Math.max(0, s.spawnDelay - dt);
    // Assommement (A29) : il s'épuise en temps réel, le temps de retour aussi.
    if (s.stunRemaining > 0) s.stunRemaining = Math.max(0, s.stunRemaining - dt);
    // *Machine Critique* : la réduction de temps de retour s'épuise.
    if (this.critBoost > 0) this.critBoost = Math.max(0, this.critBoost - dt);
    // *Tempête* (A98-4) : la tempête s'épuise en temps réel, comme Machine Critique.
    if (s.combat.stormRemaining > 0) s.combat.stormRemaining = Math.max(0, s.combat.stormRemaining - dt);

    // Poison (DoT) : peut tuer l'ennemi sans lancer.
    const enemy = s.enemy;
    if (enemy && enemy.hp > 0 && enemy.poison.length > 0) {
      const dmg = tickPoison(enemy, dt);
      if (dmg > 0) {
        const { killed } = damageEnemy(enemy, dmg);
        // Une mort par poison n'active ni Doré ni Temporel (DECISIONS D).
        if (killed) this.handleKill(enemy, 0);
      }
    }
    // La mort par poison a pu clore le combat (boss d'Invasion, 50e ennemi
    // d'Expédition — A88) : plus rien à faire couler dans ce pas de temps.
    if (!this.isCombat()) return;
    // *Mutilant* : la mutilation s'estompe (3 s).
    if (s.enemy) tickMutilation(s.enemy, dt);
    // Effets d'ennemi qui s'écoulent avec le temps (§F).
    this.tickEnemyEffects(dt);

    // *Larbin* (A18) : il lance tout seul dès que son temps de retour est
    // écoulé. Le cœur **n'applique rien** : il émet le lancer, la vue l'anime
    // puis appelle `applyThrow`. Sans vue (simulation), l'appelant applique
    // immédiatement dans son écouteur `minionThrow`.
    if (this.canThrowMinion()) {
      const result = this.rollThrow({ byMinion: true });
      if (result.id >= 0) this.emit('minionThrow', result);
      // Sans vue (simulation), l'écouteur applique aussitôt : le lancer du
      // Larbin a pu clore le combat (50e ennemi, boss — A88).
      if (!this.isCombat()) return;
    }

    // Chrono du combat : il défile ×`timeSpeed` (Poisson Lune, §F). Seul le
    // chrono est touché — les temps de retour, le poison et les chances « par
    // seconde » restent en temps réel.
    const elapsed = dt * s.combat.timeSpeed;
    s.timeSpent += elapsed;
    s.combat.durationPlayed = s.timeSpent;
    s.timeRemaining = Math.max(0, s.timeRemaining - elapsed);
    if (s.timeSpent >= s.timeMax) s.timeRemaining = 0;
    /**
     * *Boisé 3* (A94-16) : en **Invasion**, quand le chrono passe sous 5 s,
     * +10 s **une fois** par Invasion (plafond du combat respecté) ; compté
     * dans `secondsGained`.
     */
    if (
      s.phase === 'invasion' &&
      !s.combat.woodUsed &&
      s.timeRemaining < BALANCE.woodInvasionThreshold &&
      hasMaterialBonus(this.activeBonuses(), 'wood', 3)
    ) {
      s.combat.woodUsed = true;
      const added = this.addTime(BALANCE.woodInvasionSeconds);
      s.combat.secondsGained += added;
      this.emit('material', { material: 'wood', kind: 'woodSeconds', value: added });
    }
    this.timerAccum += dt;
    if (this.timerAccum >= 0.1 || s.timeRemaining === 0) {
      this.timerAccum = 0;
      this.emit('timer', { remaining: s.timeRemaining, total: s.timeTotal });
    }

    if (s.timeRemaining <= 0) {
      if (s.phase === 'expedition') phases.endExpedition(this.ctx());
      else phases.endInvasion(this.ctx(), false);
    }
  }

  /**
   * Effets d'ennemi qui s'écoulent avec le temps — §F, sur l'ennemi **vivant**
   * du moment :
   *  - **régénération** (Sanglier, Vampire, Requin, Amalgame, Plante) : part des
   *    PV max par seconde, au prorata du pas de temps ;
   *  - **Corbeau noir / Nounours affamé** : chance par seconde de retirer des
   *    secondes au chrono, jamais sous 0 ;
   *  - **assommement** (Orc Guerrier, Gros Orc, Sirène) : chance par seconde de
   *    bloquer tous les lancers 1,5 s ; non cumulable, le chrono se relance.
   *
   * Les chances « par seconde » sont converties au pas de temps par
   * `1 − (1 − p)^dt` : le résultat vaut exactement `p` sur une seconde, quel que
   * soit le découpage de la boucle.
   *
   * v0.49.0 : **tous** les effets de l'ennemi agissent (`effects`), dans leur
   * ordre ; le **Gros Ver des Sables** (`timeTick`) retire `seconds` au chrono
   * toutes les `every` secondes de combat (temps réel, comme les autres
   * effets), événement `timeDrain`. Son horloge repart à zéro à chaque nouvel
   * ennemi.
   */
  private tickEnemyEffects(dt: number): void {
    const s = this.st;
    const enemy = s.enemy;
    if (!enemy || enemy.hp <= 0) return;
    if (this.timeTickEnemy !== enemy.id) {
      this.timeTickEnemy = enemy.id;
      this.timeTickClock = 0;
    }
    for (const effect of enemyEffects(enemy)) {
      if (effect.kind === 'regen') {
        tickRegen(enemy, dt);
      } else if (effect.kind === 'timeDrain') {
        if (!this.rng.chance(chanceForDt(effect.chancePerSecond, dt))) continue;
        this.drainTime(effect.seconds);
      } else if (effect.kind === 'stun') {
        if (!this.rng.chance(chanceForDt(effect.chancePerSecond, dt))) continue;
        s.stunRemaining = effect.seconds;
        this.emit('stun', { seconds: effect.seconds });
      } else if (effect.kind === 'timeTick' && effect.every > 0) {
        this.timeTickClock += dt;
        while (this.timeTickClock >= effect.every) {
          this.timeTickClock -= effect.every;
          this.drainTime(effect.seconds);
        }
      }
    }
  }

  /**
   * Retire `seconds` au chrono (jamais sous 0) et émet `timeDrain` si du temps
   * a été perdu. Renvoie les secondes **réellement** perdues (0 sinon).
   */
  private drainTime(seconds: number): number {
    const s = this.st;
    const lost = Math.min(s.timeRemaining, seconds);
    if (lost <= 0) return 0;
    s.timeRemaining = Math.max(0, s.timeRemaining - seconds);
    this.emit('timeDrain', { seconds: lost });
    return lost;
  }

  /**
   * **Chrono de partie** (COMMENTS v0.6.0 §2) : secondes passées **hors
   * combat**, que l'interface fait avancer pendant les menus (Accalmie, écrans
   * de fin, titre). Le combat, lui, est compté par `tick()` : le total
   * `state.stats.playSeconds` est donc « combat + menus ». Persisté.
   */
  addMenuTime(dt: number): void {
    if (!Number.isFinite(dt) || dt <= 0) return;
    this.st.stats.playSeconds += dt;
  }

  /** Temps de jeu cumulé depuis la création de la sauvegarde (s). */
  playSeconds(): number {
    return this.st.stats.playSeconds;
  }

  // ───────────────────────────────────────────────────────────────── Phases

  startRun(): void {
    phases.startRun(this.ctx());
  }

  startExpedition(): void {
    phases.startExpedition(this.ctx());
  }

  endExpedition(): void {
    phases.endExpedition(this.ctx());
  }

  startInvasion(): void {
    phases.startInvasion(this.ctx());
  }

  goToAccalmie(): void {
    const had = nodeLevel(this.st.nodes, ARTISAN_NODE_ID) >= 1;
    phases.goToAccalmie(this.ctx());
    this.afterArtisanGrant(had);
    this.announceAbyss();
  }

  startNextDay(): void {
    phases.startNextDay(this.ctx());
  }

  acknowledgeInvasionWon(): void {
    phases.acknowledgeInvasionWon(this.ctx());
    this.announceAbyss();
  }

  /**
   * Écran de victoire acquitté — « Continuer : mode infini » (phase 5, A78,
   * A82) : crédits marqués vus, puis niveau 11 (Tricorne vaincu au niveau 10)
   * ou reprise en Accalmie au niveau courant (victoire différée).
   */
  acknowledgeVictory(): void {
    const had = nodeLevel(this.st.nodes, ARTISAN_NODE_ID) >= 1;
    phases.acknowledgeVictory(this.ctx());
    this.afterArtisanGrant(had);
    this.announceAbyss();
  }

  /**
   * Tire le monde et le boss du niveau courant s'il est infini et qu'il en
   * manque un (jamais de remplacement). Sert aussi au mode debug.
   */
  ensureWorld(): void {
    phases.ensureWorld({ state: this.st, rng: this.rng });
  }

  /**
   * Retire le monde du **prochain** combat infini par la règle du monde par
   * combat (différent du monde courant, jamais deux procéduraux de suite) ;
   * le boss du niveau ne change pas. En campagne, ne fait rien. Sert au mode
   * debug (`cheat.nextWorld()`).
   */
  nextWorld(): void {
    phases.drawNextCombatWorld({ state: this.st, rng: this.rng }, worldOf(this.st));
  }

  /**
   * Défaite acquittée : démarre **directement** la run suivante (Accalmie,
   * niveau 1, jour 1) — COMMENTS v0.4.1 §1.3. L'or et les dés sont perdus
   * (sauf l'emplacement 1 avec *Conservation*), l'arbre de talents est gardé.
   */
  acknowledgeRunLost(): void {
    phases.acknowledgeRunLost(this.ctx());
    this.invalidateStats();
  }

  /**
   * **Abandonner la run** — COMMENTS v0.7.0 §3 / arbitrage **A30** : depuis une
   * Expédition comme depuis une Invasion, l'abandon vaut **défaite** (écran de
   * fin de run, or en attente perdu, run comptée, `lastRun.abandoned`). Aucune
   * fenêtre de grâce : les dés en vol ne sauvent pas la run. Renvoie `false`
   * hors combat. La confirmation est du ressort de l'interface.
   */
  abandonRun(): boolean {
    return phases.abandonRun(this.ctx());
  }

  /**
   * « Aller au devant » (spec §11.1.4) : déclenche l'Invasion en avance depuis
   * l'Accalmie. Les jours restants ne sont pas joués. Renvoie `false` hors
   * Accalmie ou quand l'Invasion est déjà la prochaine sortie.
   */
  startInvasionEarly(): boolean {
    return phases.startInvasionEarly(this.ctx());
  }

  // ─────────────────────────────────────────────────────────── Mur payant

  /**
   * Pose l'état « acheté » (v0.55.0, A112) — achat Google Play, code maison
   * ou debug, relu par l'interface au lancement. Émet `entitlement` s'il
   * change ; ne change pas de phase (le niveau dû se réclame par
   * `claimGate()`). Jamais sauvegardé.
   */
  setEntitled(on: boolean): void {
    const entitled = on === true;
    if (this.st.entitled === entitled) return;
    this.st.entitled = entitled;
    this.emit('entitlement', { entitled });
  }

  /** Le jeu est-il acheté (A112) ? */
  isEntitled(): boolean {
    return this.st.entitled;
  }

  /** État du mur payant (A112) : acheté, niveau dû, joueur bloqué. */
  gateStatus(): GateStatus {
    return gateStatusOf(this.st);
  }

  /**
   * Ouvre l'écran d'achat depuis l'Accalmie d'un joueur bloqué (A112).
   * Renvoie `false` hors Accalmie ou si rien n'est fermé (jeu acheté).
   */
  openGate(): boolean {
    return phases.openGate(this.ctx());
  }

  /** Ferme l'écran d'achat : retour à l'Accalmie, même niveau (A112). */
  dismissGate(): boolean {
    return phases.dismissGate(this.ctx());
  }

  /**
   * Réclame le niveau **dû** après achat (A112) : passage au niveau suivant
   * sans rebattre le boss, depuis l'écran d'achat ou l'Accalmie. Renvoie
   * `false` si le jeu n'est pas acheté ou si rien n'est dû.
   */
  claimGate(): boolean {
    const ok = phases.claimGate(this.ctx());
    if (ok) this.announceAbyss();
    return ok;
  }

  // ────────────────────────────────────────────────────────────── Contenant

  /** Le joueur possède-t-il un contenant (nœud *Gobelet*) ? */
  hasContainer(): boolean {
    return container.hasContainer(this.st.nodes);
  }

  /** Nombre de dés Basiques du contenant (0 sans *Gobelet*) — toujours plein. */
  containerCapacity(): number {
    return container.containerCapacity(this.st.nodes);
  }

  /** Contenu du contenant regroupé (d6 puis d4) — entièrement dérivé de l'arbre. */
  containerSummary(): ContainerEntry[] {
    return container.containerSummary(this.st.nodes);
  }

  /** Nombre de d6 du contenant (*Contenant hexagonal*). */
  containerD6Count(): number {
    return container.containerD6Count(this.st.nodes);
  }

  /** Nombre de d12 du contenant (*Dodécaédrique*) ; le reste est en d4. */
  containerD12Count(): number {
    return container.containerD12Count(this.st.nodes);
  }

  /** Nombre de d20 du contenant (*Icosaèdrique*, A72-8). */
  containerD20Count(): number {
    return container.containerD20Count(this.st.nodes);
  }

  /**
   * Nom affiché du contenant : *Brouette*, *Seau*, *Bol* ou *Gobelet* selon le
   * plus grand contenant débloqué ; `null` sans contenant.
   */
  containerName(): string | null {
    return container.containerName(this.st.nodes);
  }

  // ─────────────────────────────────────── Tractopelle (A72-17)

  /** Un *Dé du chef de chantier* est-il équipé ? (le bouton du HUD en dépend) */
  hasBackhoe(): boolean {
    return this.equippedDice().some((d) => hasAffix(d, 'foreman'));
  }

  /** Dés Basiques en réserve dans le tractopelle. */
  backhoeCount(): number {
    return this.st.backhoe;
  }

  /** Le prochain lancer est-il un lancer de tractopelle ? */
  isBackhoeArmed(): boolean {
    return this.st.backhoeArmed;
  }

  /**
   * « Lancer de tractopelle » : **arme** le prochain lancer normal (manuel,
   * auto ou *Larbin*), qui emportera les dés du tractopelle en plus du
   * contenant, puis videra le compteur. Refusé (`false`) sans *Dé du chef de
   * chantier* équipé ou tractopelle vide. Idempotent.
   */
  armBackhoe(): boolean {
    if (!this.hasBackhoe() || this.st.backhoe <= 0) return false;
    this.st.backhoeArmed = true;
    this.emit('backhoe', { count: this.st.backhoe, armed: true });
    return true;
  }

  /** Désarme le lancer de tractopelle (le compteur est conservé). */
  disarmBackhoe(): boolean {
    if (!this.st.backhoeArmed) return false;
    this.st.backhoeArmed = false;
    this.emit('backhoe', { count: this.st.backhoe, armed: false });
    return true;
  }

  /** *Guinsoo* : lancers comptés pour ce dé depuis son dernier double coup (0..2). */
  guinsooCount(dieId: string): number {
    return this.st.guinsoo[dieId] ?? 0;
  }

  private emitContainer(): void {
    this.emit('container', {
      size: this.st.container.length,
      capacity: this.containerCapacity(),
    });
  }

  // ─────────────────────────────────────────────────────────────── Accalmie

  canBuyNode(id: string): NodeAvailability {
    return canBuyNodeFn(this.st.nodes, this.st.gold, id, this.balance(), this.st.stats.bestLevel);
  }

  buyNode(id: string): boolean {
    if (!this.canBuyNode(id).ok) return false;
    shop.buyNode(this.st, id, this.balance());
    this.invalidateStats();
    this.emit('gold', { gold: this.st.gold });
    this.emit('purchase', { nodeId: id });
    if (CONTAINER_NODES.has(id)) this.emitContainer();
    return true;
  }

  buyGacha(machine: MachineId, qty: 1 | 5 | 10 | 20 | 50 | 100): MasterDie[] | null {
    const out = shop.buyGacha(this.st, machine, qty, this.getDerivedStats(), this.rng, this.balance());
    if (!out) return null;
    this.emit('gold', { gold: this.st.gold });
    this.emit('purchase', { dice: out.dice });
    return out.dice;
  }

  /**
   * Prix d'un lot (remise « Négociateur » incluse), pour l'affichage. Une
   * *Corrupted GachaDice* (A94-13) : somme des prix unitaires successifs.
   */
  gachaCost(machine: MachineId, qty: number): number {
    const def = machineById(machine);
    if (!def) return Infinity;
    if (def.corrupted) {
      return shop.corruptedLotCostFor(this.st, machine, qty, this.getDerivedStats(), this.balance());
    }
    return lotCost(def, qty, this.getDerivedStats(), this.balance());
  }

  equipDie(dieId: string, slot: number): boolean {
    this.syncSlots();
    return shop.equipDie(this.st, dieId, slot);
  }

  /**
   * Verdict d'équipement, avec la raison d'un refus en français — un seul
   * exemplaire de chaque dé Prismatique nommé équipé à la fois (A72-14).
   */
  canEquipDie(dieId: string, slot: number): EquipCheck {
    this.syncSlots();
    return shop.canEquipDie(this.st, dieId, slot);
  }

  unequipDie(slot: number): boolean {
    return shop.unequipDie(this.st, slot);
  }

  /** Le Recycleur est-il débloqué ? */
  canRecycle(): boolean {
    return shop.canRecycle(this.st);
  }

  recycleDie(dieId: string): number {
    const value = shop.recycleDie(this.st, dieId);
    if (value > 0) this.emit('gold', { gold: this.st.gold });
    return value;
  }

  /**
   * Recycle tous les dés **non équipés** correspondant au filtre — COMMENTS
   * v0.2.3 §3. Chaque champ renseigné doit correspondre ; un filtre vide
   * recycle tout l'inventaire non équipé.
   */
  recycleWhere(filter: RecycleFilter = {}): RecycleOutcome {
    const out = shop.recycleWhere(this.st, filter);
    if (out.gold > 0) this.emit('gold', { gold: this.st.gold });
    return out;
  }

  /** Aperçu d'un recyclage en masse (aucun changement d'état). */
  previewRecycle(filter: RecycleFilter = {}): RecycleOutcome {
    return shop.previewRecycle(this.st, filter);
  }

  /** Recycle tous les dés Communs non équipés. Renvoie l'or total obtenu. */
  recycleAllCommon(): number {
    return this.recycleWhere({ rarity: 'common' }).gold;
  }

  /** Le meilleur dé de l'inventaire (score de dégâts attendus). */
  bestInventoryDie(): MasterDie | null {
    return bestDie(this.st.inventory, this.getDerivedStats());
  }

  /** Équipe automatiquement les meilleurs dés de l'inventaire. */
  equipBest(): void {
    this.syncSlots();
    shop.equipBest(this.st, this.getDerivedStats());
  }

  // ──────────────────────────────────────────────── Forge de dés (debug)

  /**
   * Forge un dé et le range dans l'inventaire — COMMENTS §7.
   * Les valeurs d'affixe sont bornées **largement** (`[min, max × 3]`) pour
   * permettre des tests de stress ; un identifiant d'affixe inconnu est ignoré.
   * Attention : une sauvegarde reborne les valeurs à `[min, max]` (save.ts).
   */
  forgeDie(spec: ForgeSpec): MasterDie {
    const sides: DieSides =
      spec.sides === 100
        ? 100
        : spec.sides === 20
          ? 20
          : spec.sides === 12
            ? 12
            : spec.sides === 6
              ? 6
              : 4;
    const affixes: Affix[] = [];
    for (const a of spec.affixes ?? []) {
      const def = AFFIX_BY_ID[a.id];
      if (!def) continue;
      const raw = typeof a.value === 'number' && Number.isFinite(a.value) ? a.value : def.min;
      affixes.push({ id: a.id, value: Math.min(def.max * 3, Math.max(def.min, raw)) });
    }
    const die = createMasterDie(sides, spec.rarity, affixes, this.rng, spec.spells);
    // Dé nommé forgé (A72-14) : il prend le nom fixe de son affixe unique.
    const named = namedAffixOf(die);
    if (named) die.name = namedDieName(named) ?? die.name;
    if (spec.name && spec.name.trim()) die.name = spec.name.trim();
    // A94 : niveau (1 … 60, comme la relecture), Matériau, Enrichi.
    if (typeof spec.level === 'number' && Number.isFinite(spec.level) && spec.level >= 1) {
      die.level = Math.min(60, Math.floor(spec.level));
    }
    if (isMaterialId(spec.material)) die.material = spec.material;
    if (spec.enriched === true) die.enriched = true;
    // A98-6 : Corrupted seulement sur demande. Un dé à niveau, Matériau ou
    // Enrichi non Corrupted porte `corrupted: false`, pour que la relecture
    // (qui rend Corrupted un tel dé sans le champ) ne le change pas.
    if (spec.corrupted === true) die.corrupted = true;
    else if (die.level !== undefined || die.material !== undefined || die.enriched !== undefined) {
      die.corrupted = false;
    }
    this.st.inventory.push(die);
    return die;
  }

  // ───────────────────────────────────────── Mode infini : l'Abîme (A94)

  /** La Forge est-elle ouverte (*Artisan des dés* possédé) ? */
  hasForge(): boolean {
    return nodeLevel(this.st.nodes, ARTISAN_NODE_ID) >= 1;
  }

  /**
   * Offre *Artisan des dés* s'il n'est pas possédé (A94-2) — idempotent.
   * Renvoie `true` s'il vient d'être accordé (et émet `purchase`).
   */
  grantArtisan(): boolean {
    if (!grantArtisanLevel(this.st.nodes)) return false;
    this.invalidateStats();
    this.emit('purchase', { nodeId: ARTISAN_NODE_ID });
    return true;
  }

  /** Après une transition qui a pu poser *Artisan des dés* : stats et événement. */
  private afterArtisanGrant(had: boolean): void {
    if (had || !this.hasForge()) return;
    this.invalidateStats();
    this.emit('purchase', { nodeId: ARTISAN_NODE_ID });
  }

  /**
   * Annonce les portes de l'Abîme ouvertes depuis la dernière annonce
   * (événement `abyss`, A94-3) quand le meilleur niveau atteint a monté.
   */
  private announceAbyss(): void {
    const best = this.st.stats.bestLevel;
    if (best <= this.abyssAnnounced) return;
    const opened = abyssOpenings(this.st.nodes, this.abyssAnnounced, best);
    this.abyssAnnounced = best;
    if (opened.length > 0) this.emit('abyss', { opened });
  }

  /**
   * **Exp(L)** — une Expédition du niveau L en or (A94), au multiplicateur
   * d'or de l'arbre ; niveau courant par défaut.
   */
  expeditionGoldRef(level: number = this.st.level): number {
    return expeditionGoldRefFn(level, this.getDerivedStats().goldMult, this.balance());
  }

  /**
   * Porte du **prochain** niveau achetable d'un nœud de l'Abîme (A94-3) :
   * niveau d'Expédition et ouverture (meilleur niveau atteint). `null` hors
   * Abîme ou nœud au maximum.
   */
  abyssGate(nodeId: string): { level: number; open: boolean } | null {
    const def = NODE_BY_ID[nodeId];
    const owned = nodeLevel(this.st.nodes, nodeId);
    if (!def?.gate || owned >= def.maxLevel) return null;
    const level = abyssGateLevel(def, owned) as number;
    return { level, open: this.st.stats.bestLevel >= level };
  }

  /** Facteur de dégâts du niveau d'un dé : `dieLevelFactor^niveau` (×1 sans niveau, A94-5 ; 2 par niveau depuis A110). */
  dieLevelFactor(die: MasterDie): number {
    return dieLevelFactorFn(die, this.getDerivedStats());
  }

  /** Plafond de niveau des dés : 20 + 2 × *Marteau Trempé* (A94-5). */
  dieLevelMax(): number {
    return this.getDerivedStats().dieLevelMax;
  }

  /** Valeur d'un affixe telle que le combat l'applique (× 1,5 sur un dé Enrichi). */
  affixValueOf(die: MasterDie, affix: Affix): number {
    return affixValueOfFn(die, affix);
  }

  /** Les *Corrupted GachaDice* ouvertes (A94-13) : `[]` tant que la Forge est fermée. */
  corruptedMachines(): MachineDef[] {
    if (!this.hasForge()) return [];
    return MACHINES.filter((m) => m.corrupted === true && isMachineUnlocked(this.st.nodes, m));
  }

  /** Prix du **prochain** dé d'une *Corrupted GachaDice* ; `Infinity` pour une autre machine. */
  corruptedUnitCost(machine: MachineId): number {
    const def = machineById(machine);
    if (!def?.corrupted) return Infinity;
    return this.gachaCost(machine, 1);
  }

  // ───────────────────────────────────────────────────── La Forge (A94)

  private forgeEnv(): forge.ForgeEnv {
    return { state: this.st, stats: this.getDerivedStats(), bal: this.balance(), rng: this.rng };
  }

  /**
   * Le dé peut-il être **forgé** (cible d'une recette) ? Refus en français :
   * « Seuls les dés des Corrupted GachaDice se forgent. » pour un dé de
   * campagne (A98-6), puis le Dé de l'aïeul. Accepte un dé ou son identifiant.
   */
  isForgeable(die: MasterDie | string): { ok: boolean; reason?: string } {
    const target = typeof die === 'string' ? this.st.inventory.find((d) => d.id === die) : die;
    return forge.forgeableCheck(target ?? null);
  }

  /** Les six recettes, ouvertes ou non, dans l'ordre de `FORGE_RECIPES`. */
  forgeRecipes(): ForgeRecipeView[] {
    return forge.forgeRecipeViews(this.st.nodes);
  }

  /**
   * Prix d'une recette (remise de *Marteau Trempé* comprise) ; avec un dé, le
   * prix de son prochain usage. `Infinity` si la recette ou le dé est inconnu.
   */
  forgePrice(recipe: ForgeRecipeId, dieId?: string): number {
    let die: MasterDie | null = null;
    if (dieId !== undefined) {
      die = this.st.inventory.find((d) => d.id === dieId) ?? null;
      if (!die) return Infinity;
    }
    return forge.forgePrice(recipe, die, this.getDerivedStats(), this.balance());
  }

  /** Dés consommables par une recette sur `dieId`, du moins bon au meilleur. */
  forgeCandidates(recipe: ForgeRecipeId, dieId: string, affixId?: AffixId): MasterDie[] {
    return forge.forgeCandidates(this.st, this.getDerivedStats(), recipe, dieId, affixId);
  }

  /** Verdict d'une demande de forge, avec la raison d'un refus en français et le prix. */
  forgeCheck(req: ForgeRequest): { ok: boolean; reason?: string; cost: number } {
    return forge.forgeCheck(this.forgeEnv(), req);
  }

  /**
   * Applique une recette : or débité, dés sacrifiés retirés, dé modifié ;
   * émet `gold` puis `forge`. `null` si refusée. Une *Relance* attend ensuite
   * `forgeRerollChoose`.
   */
  forgeApply(req: ForgeRequest): ForgeOutcome | null {
    const out = forge.forgeApply(this.forgeEnv(), req);
    if (!out) return null;
    // Le niveau d'un dé change son score, un dé fusionné remplace deux dés.
    this.syncSlots();
    this.emit('gold', { gold: this.st.gold });
    this.emit('forge', out);
    return out;
  }

  /**
   * Tranche la *Relance* en attente : `0` / `1` (option choisie) ou
   * `'keep'`. Émet `forge` avec `chosen`. `null` sans Relance en attente.
   */
  forgeRerollChoose(choice: 0 | 1 | 'keep'): MasterDie | null {
    const out = forge.forgeRerollChoose(this.st, this.rng, choice);
    if (!out) return null;
    this.emit('forge', {
      recipe: 'reroll',
      die: out.die,
      cost: 0,
      consumed: [],
      options: out.pending.options,
      chosen: choice,
    });
    return out.die;
  }

  /** *Relance* en attente de choix, ou `null`. */
  forgePending(): ForgePending | null {
    return this.st.forgePending;
  }

  // ─────────────────────────────────────────────────────────── Persistance

  toSave(): SaveData {
    return buildSave(this.st, { seed: this.rng.seed, state: this.rng.state });
  }

  /** Sauvegarde lisible (JSON indenté) et **signée** (A121), destinée à l'export. */
  exportSave(): string {
    // La signature couvre le JSON compact du corps ; l'indentation n'y change rien
    // (`verifySaveSignature` recompacte), et l'ordre des clés est conservé.
    return JSON.stringify(JSON.parse(serializeSave(this.toSave())), null, 2);
  }

  /**
   * Recharge une partie depuis un JSON exporté. **Jette** si le JSON est
   * illisible ou n'est pas un objet (contrairement à `normalizeSave`, qui
   * répare silencieusement les sauvegardes du `localStorage`).
   */
  static importSave(json: string): Game {
    if (typeof json !== 'string' || json.trim() === '') {
      throw new Error('Sauvegarde invalide : JSON vide.');
    }
    let parsed: unknown;
    try {
      parsed = JSON.parse(json);
    } catch {
      throw new Error('Sauvegarde invalide : JSON illisible.');
    }
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      throw new Error('Sauvegarde invalide : objet attendu.');
    }
    const data = normalizeSave(parsed);
    if (!data) {
      throw new Error(`Sauvegarde invalide : version obsolète (version ${SAVE_VERSION} attendue).`);
    }
    // A121 : un export retouché est chargé, mais marqué debug (collant).
    if (verifySaveSignature(parsed) === 'bad') data.meta.tainted = true;
    return new Game(data);
  }
}
